import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { AlertCircle, Archive, ArchiveRestore, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScoutRunStatusBadge } from '@/components/scout/ScoutStatusBadge';
import { ScoutRunSheet } from '@/components/scout/ScoutRunSheet';
import {
  useArchiveScoutRun,
  useDeleteScoutRun,
  useScoutPrograms,
  useScoutRuns,
} from '@/hooks/useScout';
import type { ScoutRunStatus, ScoutRunSummary } from '@repo/types';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/admin/scout/runs')({
  component: ScoutRunsPage,
});

/**
 * Persisted auto-refresh preference, matching the Scraping Queue's control.
 *
 * Defaults ON here, where that page defaults off: a scout run is driven by a
 * browser extension on someone else's machine, so this page is the only view of
 * a crawl in flight and a frozen progress bar is the common case it exists to
 * avoid. Its own storage key — the two pages are watched at different times.
 */
const autoRefreshAtom = atomWithStorage('oubo-scout-runs-auto-refresh', true);

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'running', label: 'Running' },
  { value: 'finished', label: 'Finished' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Tasks that will not be attempted again, over the run's total. */
function settledCount(counts: ScoutRunSummary['taskCounts']): number {
  return counts.done + counts.failed + counts.cancelled;
}

function totalCount(counts: ScoutRunSummary['taskCounts']): number {
  return counts.pending + counts.running + settledCount(counts);
}

function ScoutRunsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Archived runs are a separate view of the same list, not a row flag mixed in
  // with the active ones — otherwise "hidden" would not actually hide anything.
  const [showArchived, setShowArchived] = useState(false);
  // The run the delete dialog is confirming, if any.
  const [runToDelete, setRunToDelete] = useState<ScoutRunSummary | null>(null);
  // The run whose per-site breakdown is open, if any.
  const [openRun, setOpenRun] = useState<ScoutRunSummary | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [live, setLive] = useAtom(autoRefreshAtom);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: programs } = useScoutPrograms();
  const programNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const program of programs ?? []) map.set(program.id, program.name);
    return map;
  }, [programs]);

  const filters = useMemo(
    () => ({
      status: statusFilter !== 'all' ? (statusFilter as ScoutRunStatus) : undefined,
      archived: showArchived,
    }),
    [statusFilter, showArchived]
  );

  const archiveRun = useArchiveScoutRun();
  const deleteRun = useDeleteScoutRun();

  const { data, isLoading, isFetching, error, refetch } = useScoutRuns(
    filters,
    { page, limit: pageSize },
    {
      placeholderData: keepPreviousData,
      // A run is driven by a browser extension on someone else's machine, so
      // there is no server-side push to wait on — polling is the only way this
      // page learns that a crawl advanced. 10s while one is in flight, 30s
      // otherwise (a run can only start from the extension, so an idle list
      // still changes, just rarely). Polling stops when this page unmounts and
      // pauses while the tab is backgrounded.
      refetchInterval: live
        ? (query) =>
            query.state.data?.items?.some((run: ScoutRunSummary) => run.status === 'running')
              ? 10000
              : 30000
        : false,
      refetchIntervalInBackground: false,
    }
  );

  const runs = useMemo(() => data?.items ?? [], [data]);

  const columns = useMemo<ColumnDef<ScoutRunSummary>[]>(
    () => [
      {
        accessorKey: 'startedAt',
        header: 'Started',
        cell: ({ row }) => (
          <div className="space-y-0.5">
            <p className="font-medium">
              {format(new Date(row.original.startedAt), 'd MMM yyyy, HH:mm')}
            </p>
            <p className="text-xs text-muted-foreground">
              {row.original.employeeName || row.original.employeeId.slice(0, 8)}
            </p>
          </div>
        ),
      },
      {
        id: 'programs',
        header: 'Programs',
        cell: ({ row }) => {
          const names = row.original.programIds
            .map((id) => programNameById.get(id))
            .filter(Boolean) as string[];
          return (
            <div className="max-w-[240px] space-y-0.5">
              <p className="truncate text-sm" title={names.join(', ')}>
                {names.length ? names.join(', ') : '—'}
              </p>
              {row.original.roleFilter && (
                <p className="truncate text-xs text-muted-foreground">
                  Role: {row.original.roleFilter}
                </p>
              )}
              {row.original.locations.length > 0 && (
                <p
                  className="truncate text-xs text-muted-foreground"
                  title={row.original.locations.join(', ')}
                >
                  In: {row.original.locations.join(', ')}
                </p>
              )}
            </div>
          );
        },
      },
      {
        id: 'progress',
        header: 'Progress',
        cell: ({ row }) => {
          const counts = row.original.taskCounts;
          const total = totalCount(counts);
          const settled = settledCount(counts);
          const percent = total ? Math.round((settled / total) * 100) : 0;
          return (
            <div className="w-[160px] space-y-1.5">
              <Progress value={percent} className="h-1.5" />
              {/* The counts are the way in to the per-site breakdown: an
                  operator who sees "2 failed" wants to know which two, and this
                  is where they will click to find out. */}
              <button
                type="button"
                onClick={() => setOpenRun(row.original)}
                className="text-xs text-muted-foreground hover:underline"
                title="See what happened to each site"
              >
                {settled}/{total} sites
                {counts.failed > 0 && (
                  <span className="text-rose-600 dark:text-rose-400"> · {counts.failed} failed</span>
                )}
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'postingCount',
        header: 'Postings',
        cell: ({ row }) =>
          row.original.postingCount > 0 ? (
            <Link
              to="/admin/scout/postings"
              search={{ runId: row.original.id }}
              className="font-medium hover:underline"
            >
              {row.original.postingCount}
            </Link>
          ) : (
            <span className="text-muted-foreground">0</span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <ScoutRunStatusBadge status={row.original.status} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const run = row.original;
          // A live crawl is still writing results into this run, so neither
          // action is offered until it settles.
          const isRunning = run.status === 'running';
          const isArchived = Boolean(run.archivedAt);

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-8" aria-label="Run actions">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={isRunning || archiveRun.isPending}
                  onClick={() => archiveRun.mutate({ id: run.id, archived: !isArchived })}
                >
                  {isArchived ? (
                    <>
                      <ArchiveRestore className="size-4" />
                      Restore to list
                    </>
                  ) : (
                    <>
                      <Archive className="size-4" />
                      Archive
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  disabled={isRunning}
                  onClick={() => setRunToDelete(run)}
                >
                  <Trash2 className="size-4" />
                  Delete run
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [programNameById, archiveRun]
  );

  const table = useReactTable({
    data: runs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  // Guarantee a visible spin even when the refetch resolves instantly (cached).
  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    window.setTimeout(() => setIsRefreshing(false), 600);
  };
  const refreshing = isRefreshing || isFetching;

  const total = data?.total ?? 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Scout Runs"
        description="Crawls started from the Job Scout extension. One task per company career site."
        actions={
          <>
            <Label
              htmlFor="live"
              className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground"
            >
              <span
                className={cn(
                  'size-2 rounded-full',
                  live ? 'animate-pulse bg-emerald-500' : 'bg-muted-foreground/40'
                )}
              />
              Auto-refresh
            </Label>
            <Switch id="live" checked={live} onCheckedChange={setLive} />
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="flex items-center justify-end gap-3">
        <Label
          htmlFor="archived"
          className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Archive className="size-3.5" />
          Archived
        </Label>
        <Switch
          id="archived"
          checked={showArchived}
          onCheckedChange={(value) => {
            setShowArchived(value);
            setPage(1);
          }}
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message || 'Failed to load scout runs.'}</AlertDescription>
        </Alert>
      )}

      {/*
        Deletion is confirmed against its exact blast radius, not a generic "are
        you sure": this cascades to every site result and posting the run
        produced, and a count is the only honest way to say how much that is.

        The server independently refuses a running run and any run holding
        promoted postings — this dialog is the courtesy, not the guard.
      */}
      <AlertDialog open={!!runToDelete} onOpenChange={() => setRunToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this run?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {runToDelete
                    ? `Started ${format(new Date(runToDelete.startedAt), 'd MMM yyyy, HH:mm')}`
                    : ''}
                  {runToDelete?.roleFilter ? ` · role “${runToDelete.roleFilter}”` : ''}
                </p>
                <p>
                  This permanently removes the run,{' '}
                  <span className="font-medium text-foreground">
                    {runToDelete ? totalCount(runToDelete.taskCounts) : 0} site result
                    {runToDelete && totalCount(runToDelete.taskCounts) === 1 ? '' : 's'}
                  </span>{' '}
                  and{' '}
                  <span className="font-medium text-foreground">
                    {runToDelete?.postingCount ?? 0} posting
                    {runToDelete?.postingCount === 1 ? '' : 's'}
                  </span>
                  , including any still awaiting review. This cannot be undone.
                </p>
                <p>Archive it instead if you only want it out of the list.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleteRun.isPending}
              onClick={() => {
                if (runToDelete) deleteRun.mutate(runToDelete.id);
                setRunToDelete(null);
              }}
            >
              Delete run
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isLoading ? (
        <DataTableSkeleton columns={6} />
      ) : (
        <>
          <DataTable table={table} />

          {total > 0 && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{total}</span> run
                {total === 1 ? '' : 's'}
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rows</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[72px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[20, 50].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <ScoutRunSheet run={openRun} onOpenChange={(open) => !open && setOpenRun(null)} />
    </div>
  );
}
